import moment from 'moment';
import 'moment-timezone';
import { all_routes } from '../core/data/routes/all_routes';

export const ANY_STAFF_ID = 'ANY_STAFF';
export const ANY_STAFF_LABEL = 'Any Staff';

/**
 * Resolve the booking-entry route for a service based on its `bookingType`.
 * Defaults to the SLOT flow when `bookingType` is absent (backwards compatible
 * with Phase 1 pre-split services).
 *
 * - SLOT    → existing slot-booking flow
 * - QUOTE   → Job Request Form
 * - PACKAGE → Package Selection
 *
 * The returned path contains a `:serviceId` placeholder for QUOTE/PACKAGE; the
 * SLOT path is static (`/service/booking`) so `.replace(':serviceId', id)` is a
 * safe no-op for it.
 */
export const resolveBookingRoute = (bookingType?: string | null): string => {
  const type = (bookingType || 'SLOT').toUpperCase();
  if (type === 'QUOTE') return all_routes.serviceJobRequest;
  if (type === 'PACKAGE') return all_routes.servicePackage;
  return all_routes.serviceBooking;
};

/**
 * Generate a UUID v4 used as the booking idempotency key.
 *
 * Generated ONCE per submit intent (not per retry) so the backend can dedupe
 * duplicate submits (double-click, axios auto-retry) via a unique sparse index.
 * Prefers the Web Crypto API (`crypto.randomUUID`), which is available in modern
 * browsers on secure contexts and in Node >= 14.17 (Vitest). Falls back to a
 * Math.random-based v4 when `randomUUID` is unavailable (non-secure contexts).
 */
export const generateIdempotencyKey = (): string => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // RFC 4122 v4 fallback — Math.random is not cryptographically secure, but
  // this is only a dedupe key, not an auth secret.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

interface BookingPayload {
  additonalServiceIds: string[];
  timeAndStaff: any;
  personalDetails: any;
  service: any;
  calculation: any;
  packageId: string;
  paymentType: string;
}

//VALIDTE BOOKING PAYLOAD
// QA-36: `data` (the formatted booking payload) no longer carries
// subtotal/tax/discount/total -- pms rejects a create-booking request
// that includes any of them with a 422 (computed server-side only), so
// formatBookingPayload() stops sending them. The price sanity checks
// below are still worth keeping as a pre-submit UX guard (catch an
// obviously-broken $0/negative total before a network round trip), so
// they now check the locally computed `calculation` object directly --
// the same source formatBookingPayload used to copy those fields from --
// instead of `data`, where those keys no longer exist.
export const validateBookingPayload = async (data: any, calculation?: any) => {
  if (!data.providerId) {
    console.error('Error: Provider Id is missing');
    throw new Error('Provider Id is missing, please try again');
  }

  if (!data?.serviceId) {
    console.error('Error: Service Id is missing');
    throw new Error('Service Id is missing, please try again');
  }

  if (!data?.appointmentDate) {
    console.error('Error: Date is missing');
    throw new Error('Please select a date');
  }

  if (
    !data?.timeSlotId ||
    !data?.appointmentTimeFrom ||
    !data?.appointmentTimeTo
  ) {
    console.error('Error: Time slot is missing');
    throw new Error('Please select a appoitment time');
  }

  if (!data?.staffId) {
    console.error('Error: Staff Id is missing');
    throw new Error('Please select a staff');
  }

  // Explicit presence check before the NaN-comparison checks below: a
  // NaN comparison (`Number(undefined) <= 0`) is always false, so an
  // entirely-missing `calculation` would otherwise silently pass every
  // check that follows instead of failing closed.
  if (!calculation) {
    console.error('Error: Price calculation is missing');
    throw new Error('Price calculation is missing, please try again');
  }

  if (Number(calculation?.total) <= 0) {
    console.error('Error: Total value is 0 or negative');
    throw new Error('Totat amount is missing, please try again');
  }

  if (Number(calculation?.subtotal) <= 0) {
    console.error('Error: Subtotal value is 0 or negative');
    throw new Error('Subtotal  is missing, please try again');
  }

  if (Number(calculation?.tax) < 0) {
    console.error('Error: Tax value is  negative');
    throw new Error('Tax is negative, please try again');
  }

  if (Number(calculation?.discount) < 0) {
    console.error('Error: Discount is negative');
    throw new Error('Discount is negative, please try again');
  }

  if (!data?.personalInfo?.firstName) {
    console.error('Error: First name is missing');
    throw new Error('Your name is missing. Please try again');
  }

  if (!data?.personalInfo?.email) {
    console.error('Error: Email is missing');
    throw new Error('Email is missing. Please try again');
  }

  if (!data?.personalInfo?.phone) {
    console.error('Error: Phone is missing');
    throw new Error('Contact no is missing. Please try again');
  }

  if (!data?.personalInfo?.address) {
    console.error('Error: Address is missing');
    throw new Error('Please enter your address');
  }

  if (!data?.personalInfo?.address.street) {
    console.error('Error: Street is missing');
    throw new Error('Please enter your street');
  }

  if (!data?.personalInfo?.address.city) {
    console.error('Error: City is missing');
    throw new Error('Please enter your city');
  }

  if (!data?.personalInfo?.address.state) {
    console.error('Error: State is missing');
    throw new Error('Please enter your state');
  }

  if (!data?.personalInfo?.address.postalCode) {
    console.error('Error: Postal code is missing');
    throw new Error('Please enter your postal code code');
  }
};

//Format booking payload
export const formatBookingPayload = ({
  additonalServiceIds,
  timeAndStaff,
  personalDetails,
  service,
  calculation,
  packageId,
  paymentType,
}: BookingPayload) => {
  const payload = {
    providerId: service?.providerId,
    packageId: packageId || '',
    locationId: '',
    staffId: timeAndStaff?.staffId,
    additionalServiceIds: additonalServiceIds,
    appointmentDate: timeAndStaff?.date,
    timeSlotId: timeAndStaff?.timeSlotId,
    appointmentTimeFrom: timeAndStaff?.start,
    appointmentTimeTo: timeAndStaff?.end,
    personalInfo: {
      firstName: personalDetails?.firstName,
      lastName: personalDetails?.lastName || '',
      email: personalDetails?.email,
      phone: personalDetails?.phone,
      address: {
        street: personalDetails?.address?.street,
        city: personalDetails?.address?.city,
        state: personalDetails?.address?.state,
        postalCode: personalDetails?.address?.postalCode,
      },
      bookingNotes: personalDetails?.bookingNotes,
    },
    serviceId: service?.serviceId,
    // 'Online Payment' is the FE selector label, not a valid product-side
    // paymentMethod (bookingMiddleware rejects it). Map it to the Stripe
    // paymentMethod so the booking is created in PENDING_CAPTURE; cash
    // stays untouched.
    paymentMethod:
      paymentType === 'Online Payment' ? 'Credit/Debit Card' : paymentType,
    discountId: calculation?.discountId,
    isPaid: false,
    // QA-36: subtotal/tax/discount/total are NOT sent -- pms now rejects a
    // create-booking request that includes any of them with a 422
    // ("Pricing fields ... are computed server-side and must not be
    // supplied by the client"), and that guard is correct: it prevents a
    // tampered/stale client total from ever being trusted. The locally
    // computed `calculation` is still used for the on-screen price
    // summary and for `discountId` above -- it's just never sent as the
    // authoritative price. The confirmed price comes back on the booking
    // creation response (see buildBookingSuccessState, which already
    // reads `booking.subtotal`/`.tax`/`.total`/`.discount`, not this
    // payload).
  };
  return payload;
};

/**
 * Build the final create payload for POST /booking.
 *
 * Attaches the held-slot token (when a hold was successfully acquired) and the
 * idempotency key. The key is minted once per slot/staff selection (see the
 * `idempotencyKeyRef` wiring in BookingPage) and reused for the lifetime of
 * that submit intent, so an axios auto-retry — or a user re-click of the same
 * slot — reuses the same key and the backend dedupes via its unique
 * `idempotencyKey` index.
 */
export const buildBookingPayload = (
  payload: any,
  hold: { holdToken?: string } | null,
  idempotencyKey: string
): any => ({
  ...payload,
  ...(hold?.holdToken ? { holdToken: hold.holdToken } : {}),
  idempotencyKey,
});

/**
 * Merge the payment-service tax quote into the booking-total calculation.
 *
 * The booking `calculateTotals` response carries subtotal/discount but no tax;
 * tax comes from the payment service (/payment/api/v1/tax) and must be merged
 * in so the customer is shown — and authorized for — the correct total.
 * Returns a copy; the input object is never mutated.
 */
export const mergePaymentTax = (
  result: any,
  quote?: { totalTaxMinor?: number } | null
): any => {
  const updated = { ...result };
  if (!quote || typeof quote !== 'object') return updated;

  const minorKeys = [
    quote?.totalTaxMinor,
    (quote as any)?.taxMinor,
    (quote as any)?.total_tax_minor,
  ];

  const minorTax = minorKeys.find(
    (value) =>
      value !== undefined && value !== null && Number.isFinite(Number(value))
  );

  if (minorTax !== undefined) {
    const tax = Number(minorTax) / 100;
    const subtotal = Number(result?.subtotal || 0);
    const discount = Number(result?.discount || 0);
    updated.tax = tax;
    updated.total = subtotal - discount + tax;
    return updated;
  }

  const directTax = Number(
    (quote as any)?.totalTax ??
      (quote as any)?.tax ??
      (quote as any)?.amountTax ??
      0
  );

  if (!Number.isFinite(directTax)) return updated;

  const subtotal = Number(result?.subtotal || 0);
  const discount = Number(result?.discount || 0);
  updated.tax = directTax;
  updated.total = subtotal - discount + directTax;
  return updated;
};

// CHEACK DISCOUNT IS VALID
export const checkDiscountIsValid = (discount: any) => {
  if (!discount?.IsActive) return false;
  if (!discount.amount || discount.amount <= 0) return false;

  const now = moment();
  const start = discount?.duration?.start
    ? moment(discount.duration.start)
    : null;
  const end = discount?.duration?.end ? moment(discount.duration.end) : null;
  if (start && now.isBefore(start)) return false; // not started yet
  if (end && now.isAfter(end)) return false; // already expired
  return true;
};

//ADDRESS FORMAT
export const addressFormatHelper = (addressParts: any) => {
  // Accepts either an address object or an array of addresses
  if (!addressParts) return '-';

  const pickAddress = (addr: any) => {
    if (!addr) return null;
    const parts = [
      addr?.addressLine1 || addr?.street || addr?.line1,
      addr?.addressLine2 || addr?.line2,
      addr?.city,
      stateConvertToShortCode(addr?.state || addr?.province || addr?.region),
      addr?.postalCode || addr?.zipCode || addr?.zip,
      countryConvertToShortCode(addr?.country),
    ];
    const joined = parts.filter(Boolean).join(', ');
    return joined || null;
  };

  if (Array.isArray(addressParts)) {
    const preferred =
      addressParts.find((a) => a?.isLegalAddress) || addressParts[0] || null;
    return pickAddress(preferred) || '-';
  }

  return pickAddress(addressParts) || '-';
};

const countryConvertToShortCode = (country?: string): string => {
  if (!country) return '';
  const provinces: Record<string, string> = {
    canada: 'CA',
  };

  return provinces[country.toLowerCase()] || country;
};

export const stateConvertToShortCode = (state?: string): string => {
  if (!state) return '';
  const provinces: Record<string, string> = {
    alberta: 'AB',
    'british columbia': 'BC',
    manitoba: 'MB',
    'new brunswick': 'NB',
    'newfoundland and labrador': 'NL',
    'northwest territories': 'NT',
    'nova scotia': 'NS',
    nunavut: 'NU',
    ontario: 'ON',
    'prince edward island': 'PE',
    quebec: 'QC',
    saskatchewan: 'SK',
    yukon: 'YT',
  };

  return provinces[state.toLowerCase()] || state;
};

export const calculateDiscount = (
  price?: string | number,
  discount?: string | number,
  type?: 'amount' | 'percentage'
) => {
  if (!price) return 0;
  const priceNum = Number(price);
  if (!discount || !type) return priceNum;

  const discountNum = Number(discount);
  return type === 'amount'
    ? Math.max(priceNum - discountNum, 0)
    : Math.max(priceNum - (priceNum * discountNum) / 100, 0);
};

// export const handlingPassedTime = (
//   timeSlots: string[],
//   providerTimeZone: string,
//   userSelectDate: Date
// ) => {
//   if (!timeSlots || !providerTimeZone || !userSelectDate) return [];
//   const selectedDate = moment(userSelectDate).format('YYYY-MM-DD');

//   // Calculate TODAY's date in the provider's time zone for comparison
//   const todayInProviderZone = moment()
//     .tz(providerTimeZone)
//     .format('YYYY-MM-DD');

//   // 1. Check if the selected date IS today in the provider's time zone
//   const isSelectedDateIsToday = selectedDate === todayInProviderZone;

//   // Start with the full list of slots
//   let filteredTimeSlots = timeSlots;

//   // 2. ONLY FILTER IF the selected date IS TODAY
//   if (isSelectedDateIsToday) {
//     const currentTimeInProviderZone = moment().tz(providerTimeZone);

//     filteredTimeSlots = timeSlots.filter((slot: any) => {
//       // Construct the full date-time in the provider's zone for comparison
//       const slotDateTime = moment.tz(
//         `${selectedDate} ${slot.from}`,
//         'YYYY-MM-DD hh:mm A',
//         providerTimeZone
//       );

//       // True if the slot is still in the future relative to the provider's NOW
//       return slotDateTime.isAfter(currentTimeInProviderZone);
//     });
//   }

//   return filteredTimeSlots;
// };

export const handlingPassedTime = (
  timeSlots: any[], // Assuming objects containing { from: "11:12 AM", ... }
  providerTimeZone: string,
  userSelectDate: Date
) => {
  if (!timeSlots || !providerTimeZone || !userSelectDate) return [];

  // Format the user's selected calendar date matching your backend structure
  const selectedDateStr = moment(userSelectDate).format('YYYY-MM-DD');

  // Get the absolute exact current global moment right now
  const absoluteNow = moment();

  // Filter slots by creating an absolute point in time for each slot
  return timeSlots.filter((slot: any) => {
    // 1. Construct the absolute timestamp of when this slot occurs
    const slotAbsoluteTime = moment.tz(
      `${selectedDateStr} ${slot.from}`,
      'YYYY-MM-DD hh:mm A',
      providerTimeZone
    );

    // 2. If the slot time is in the past globally compared to right now, drop it!
    // This works perfectly regardless of whether it's yesterday, today, or tomorrow.
    return slotAbsoluteTime.isAfter(absoluteNow);
  });
};

export const BOOKING_AVAILABILITY_REASON = {
  NO_ACTIVE_STAFF: 'NO_ACTIVE_STAFF',
  STAFF_NOT_AVAILABLE: 'STAFF_NOT_AVAILABLE',
} as const;

type BookingAvailabilityResponse = {
  success?: boolean;
  reason?: string;
  message?: string;
};

export const getBookingAvailabilityMessage = (
  response?: BookingAvailabilityResponse | null
): string | null => {
  if (!response) return null;

  if (response.reason === BOOKING_AVAILABILITY_REASON.NO_ACTIVE_STAFF) {
    return 'No active staff are available for this service. Please contact the provider.';
  }

  if (!response.success && response.message) {
    return response.message;
  }

  return null;
};

/**
 * Normalize a stored payment-method value into a customer-friendly label.
 *
 * The backend historically persisted the literal value `wallet` for the
 * cash-on-hand payment option. This only re-labels the value for display —
 * it deliberately does NOT change the value sent to the backend.
 */
export const formatPaymentMethod = (value?: string | null): string => {
  const v = String(value || '')
    .trim()
    .toLowerCase();
  if (!v) return String(value || '');
  if (v === 'wallet') return 'Cash on Hand';
  if (v === 'cash' || v === 'cash on hand') return 'Cash on Hand';
  if (
    v === 'card' ||
    v === 'credit' ||
    v === 'debit' ||
    v === 'credit/debit card'
  ) {
    return 'Credit/Debit Card';
  }
  if (v === 'stripe') return 'Stripe';
  return String(value);
};

/**
 * Compute the minimum tier price for a service card.
 *
 * PACKAGE-type services carry `packageTiers` (each with its own `price`). When
 * that array is absent (or empty) we fall back to `packages`, then to the flat
 * `price`. Returns `null` when no price can be derived so callers can fall back
 * to their own placeholder.
 */
export const getServiceMinTierPrice = (
  service?: {
    packageTiers?: Array<{ price?: number | string | null }>;
    packages?: Array<{ price?: number | string | null }>;
    price?: number | string | null;
  } | null
): number | null => {
  if (!service) return null;

  const collect = (items?: Array<{ price?: number | string | null }>) =>
    (items || [])
      .map((t) => Number(t?.price))
      .filter((p) => typeof p === 'number' && !Number.isNaN(p));

  const tierPrices = collect(service.packageTiers);
  if (tierPrices.length > 0) return Math.min(...tierPrices);

  const packagePrices = collect(service.packages);
  if (packagePrices.length > 0) return Math.min(...packagePrices);

  const flat = Number(service.price);
  return typeof flat === 'number' && !Number.isNaN(flat) ? flat : null;
};
